// Sharpening and deblurring on plain RGBA buffers, shared by the sharpen and unblur tools and
// run inside image-sharpen.worker.ts so big photos do not freeze the page.
//
// Both methods work on luminance only and add the change back to R, G and B equally: sharpening
// colour channels separately paints red/blue fringes along edges.
//
// The old 3x3 kernels (2026-10-06 support ticket "the output looked the same as the input") only
// touched detail one pixel wide. Phone photos blur over 2-5 pixels, so the radius has to be
// adjustable, and the result has to be viewed at 100% to be judged at all.

export type SharpenSettings = {
  /** Gaussian sigma of the blur being subtracted, in pixels. */
  radius: number;
  /** How much of the detail layer to add back; 1 doubles edge contrast. */
  amount: number;
  /** Luminance differences below this (0-255 scale) are left alone, so noise is not boosted. */
  threshold: number;
};

export type UnblurSettings = {
  /** Estimated Gaussian sigma of the blur in the photo, in pixels. */
  blur: number;
  /** Richardson-Lucy iterations; more recovers more detail and more noise. */
  iterations: number;
  /** Changes below this (0-255 scale) are faded out to keep flat areas clean. */
  noise: number;
};

type Progress = (fraction: number) => void;

function gaussianKernel(sigma: number): Float32Array {
  const radius = Math.max(1, Math.ceil(sigma * 3));
  const kernel = new Float32Array(radius * 2 + 1);
  let sum = 0;
  for (let i = -radius; i <= radius; i += 1) {
    const value = Math.exp(-(i * i) / (2 * sigma * sigma));
    kernel[i + radius] = value;
    sum += value;
  }
  for (let i = 0; i < kernel.length; i += 1) kernel[i] /= sum;
  return kernel;
}

/** Box widths whose three successive passes approximate a Gaussian of this sigma. */
function boxRadii(sigma: number): number[] {
  const ideal = Math.sqrt((12 * sigma * sigma) / 3 + 1);
  let lower = Math.floor(ideal);
  if (lower % 2 === 0) lower -= 1;
  const upper = lower + 2;
  const m = Math.round(
    (12 * sigma * sigma - 3 * lower * lower - 12 * lower - 9) / (-4 * lower - 4),
  );
  return [0, 1, 2].map((i) => ((i < m ? lower : upper) - 1) / 2);
}

/** Running-sum box blur along rows (axis 0) or columns (axis 1), edges clamped. */
function boxPass(
  src: Float32Array,
  dst: Float32Array,
  width: number,
  height: number,
  radius: number,
  axis: 0 | 1,
) {
  const lines = axis === 0 ? height : width;
  const length = axis === 0 ? width : height;
  const stride = axis === 0 ? 1 : width;
  const scale = 1 / (radius * 2 + 1);
  for (let line = 0; line < lines; line += 1) {
    const base = axis === 0 ? line * width : line;
    const first = src[base];
    const last = src[base + (length - 1) * stride];
    let acc = (radius + 1) * first;
    for (let k = 1; k <= radius; k += 1) acc += src[base + Math.min(k, length - 1) * stride];
    for (let i = 0; i < length; i += 1) {
      dst[base + i * stride] = acc * scale;
      const add = i + radius + 1;
      const remove = i - radius;
      acc +=
        (add < length ? src[base + add * stride] : last) -
        (remove >= 0 ? src[base + remove * stride] : first);
    }
  }
}

/**
 * Gaussian blur of one float plane. Small sigmas use the exact separable kernel; larger ones use
 * three box passes, which cost the same at any radius (error under 3%).
 */
export function blurPlane(
  plane: Float32Array,
  width: number,
  height: number,
  sigma: number,
): Float32Array {
  if (sigma >= 1.5) {
    const a = new Float32Array(plane);
    const b = new Float32Array(plane.length);
    for (const radius of boxRadii(sigma)) {
      if (radius < 1) continue;
      boxPass(a, b, width, height, radius, 0);
      boxPass(b, a, width, height, radius, 1);
    }
    return a;
  }
  return exactBlur(plane, width, height, sigma);
}

function exactBlur(
  plane: Float32Array,
  width: number,
  height: number,
  sigma: number,
): Float32Array {
  const kernel = gaussianKernel(sigma);
  const radius = (kernel.length - 1) / 2;
  const temp = new Float32Array(plane.length);
  const out = new Float32Array(plane.length);

  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      let acc = 0;
      for (let k = -radius; k <= radius; k += 1) {
        let sx = x + k;
        if (sx < 0) sx = 0;
        else if (sx >= width) sx = width - 1;
        acc += plane[row + sx] * kernel[k + radius];
      }
      temp[row + x] = acc;
    }
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let acc = 0;
      for (let k = -radius; k <= radius; k += 1) {
        let sy = y + k;
        if (sy < 0) sy = 0;
        else if (sy >= height) sy = height - 1;
        acc += temp[sy * width + x] * kernel[k + radius];
      }
      out[y * width + x] = acc;
    }
  }
  return out;
}

export function lumaOf(data: Uint8ClampedArray): Float32Array {
  const luma = new Float32Array(data.length / 4);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    luma[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return luma;
}

/** Soft threshold: full change above 2x the threshold, fading to none at the threshold. */
function fade(delta: number, threshold: number): number {
  if (threshold <= 0) return delta;
  const magnitude = Math.abs(delta);
  if (magnitude <= threshold) return 0;
  if (magnitude >= threshold * 2) return delta;
  return delta * ((magnitude - threshold) / threshold);
}

function applyLumaDelta(
  data: Uint8ClampedArray,
  delta: Float32Array,
  threshold: number,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(data.length);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const d = fade(delta[p], threshold);
    out[i] = data[i] + d;
    out[i + 1] = data[i + 1] + d;
    out[i + 2] = data[i + 2] + d;
    out[i + 3] = data[i + 3];
  }
  return out;
}

export function sharpenRgba(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  settings: SharpenSettings,
  onProgress?: Progress,
): Uint8ClampedArray {
  const amount = Math.max(0, settings.amount);
  if (amount === 0) return new Uint8ClampedArray(data);
  const luma = lumaOf(data);
  onProgress?.(0.2);
  const blurred = blurPlane(luma, width, height, Math.max(0.3, settings.radius));
  onProgress?.(0.8);
  const delta = new Float32Array(luma.length);
  for (let p = 0; p < luma.length; p += 1) delta[p] = amount * (luma[p] - blurred[p]);
  const out = applyLumaDelta(data, delta, settings.threshold);
  onProgress?.(1);
  return out;
}

/**
 * Richardson-Lucy deconvolution of the luminance with a Gaussian point spread function, with
 * Biggs-Andrews acceleration (extrapolate along the last step) so ~10 iterations do the work of
 * ~40 plain ones. Each correction is clamped to [0.5, 2] so ringing and noise cannot explode on
 * JPEG photos.
 */
export function unblurRgba(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  settings: UnblurSettings,
  onProgress?: Progress,
): Uint8ClampedArray {
  const iterations = Math.max(1, Math.round(settings.iterations));
  const sigma = Math.max(0.3, settings.blur);
  const observed = lumaOf(data);
  for (let p = 0; p < observed.length; p += 1) observed[p] = observed[p] / 255 + 0.01;

  const size = observed.length;
  let estimate = new Float32Array(observed);
  let previous = new Float32Array(observed);
  let lastStep: Float32Array | null = null;
  let stepBefore: Float32Array | null = null;
  const ratio = new Float32Array(size);
  const predicted = new Float32Array(size);

  for (let step = 0; step < iterations; step += 1) {
    let alpha = 0;
    if (lastStep && stepBefore) {
      let num = 0;
      let den = 0;
      for (let p = 0; p < size; p += 1) {
        num += lastStep[p] * stepBefore[p];
        den += stepBefore[p] * stepBefore[p];
      }
      alpha = den > 0 ? Math.min(0.95, Math.max(0, num / den)) : 0;
    }
    for (let p = 0; p < size; p += 1) {
      const value = estimate[p] + alpha * (estimate[p] - previous[p]);
      predicted[p] = value < 1e-4 ? 1e-4 : value;
    }

    const reblurred = blurPlane(predicted, width, height, sigma);
    for (let p = 0; p < size; p += 1) ratio[p] = observed[p] / Math.max(reblurred[p], 1e-4);
    const correction = blurPlane(ratio, width, height, sigma);
    const next = new Float32Array(size);
    const change = new Float32Array(size);
    for (let p = 0; p < size; p += 1) {
      let c = correction[p];
      if (c > 2) c = 2;
      else if (c < 0.5) c = 0.5;
      let value = predicted[p] * c;
      if (value > 1.2) value = 1.2;
      next[p] = value;
      change[p] = value - predicted[p];
    }
    stepBefore = lastStep;
    lastStep = change;
    previous = estimate;
    estimate = next;
    onProgress?.((step + 1) / iterations);
  }

  const delta = new Float32Array(observed.length);
  for (let p = 0; p < delta.length; p += 1) delta[p] = (estimate[p] - observed[p]) * 255;
  return applyLumaDelta(data, delta, settings.noise);
}

/** Mean absolute Laplacian of the luminance: a rough "how much fine detail" score. */
export function detailScore(data: Uint8ClampedArray, width: number, height: number): number {
  const luma = lumaOf(data);
  let sum = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += 2) {
    for (let x = 1; x < width - 1; x += 2) {
      const p = y * width + x;
      sum += Math.abs(4 * luma[p] - luma[p - 1] - luma[p + 1] - luma[p - width] - luma[p + width]);
      count += 1;
    }
  }
  return count ? sum / count : 0;
}
